import { createFileRoute, Link } from '@tanstack/react-router';
import { LegalPageLayout } from '@/components/legal/LegalPageLayout';

export const Route = createFileRoute('/legal/privacy')({
  component: PrivacyPolicy,
});

function PrivacyPolicy() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="February 25, 2026">
      <p>
        Grateful8 Consulting LLC ("we," "us," or "our") operates Wundra
        ("Service"), an AI assisted travel planning application. This Privacy
        Policy explains how we collect, use, disclose, and safeguard your
        information when you use our Service.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>Account Information</h3>
      <p>
        When you create an account, we collect information provided through your
        chosen authentication method:
      </p>
      <ul>
        <li>Email address and password (email sign-up)</li>
        <li>
          Name, email, and profile photo (Google or Apple sign-in via Firebase
          Authentication)
        </li>
      </ul>

      <h3>Profile Information</h3>
      <p>
        You may optionally provide additional profile details such as display
        name, travel preferences, and notification settings.
      </p>

      <h3>Trip Data</h3>
      <p>
        When you use Wundra to plan trips, we store your destinations,
        itineraries, activity selections, scheduling preferences, and any notes
        or customizations you add.
      </p>

      <h3>Payment Information</h3>
      <p>
        Payments are processed by Stripe. We do not store your full credit card
        number or payment credentials. Stripe may collect billing details
        including your name, card information, and billing address. See{' '}
        <a
          href="https://stripe.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Stripe's Privacy Policy
        </a>{' '}
        for details.
      </p>

      <h3>Usage Data</h3>
      <p>
        We collect analytics data through Firebase Analytics (when you have
        opted in) including pages visited, features used, session duration, and
        device/browser information.
      </p>

      <h3>Affiliate &amp; Referral Data</h3>
      <p>
        If you arrive via an affiliate or referral link, we store the affiliate
        code, UTM parameters, and referral source in local storage with a 60-day
        expiry for attribution purposes.
      </p>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>
          <strong>Service delivery:</strong> To create, manage, and display your
          travel itineraries
        </li>
        <li>
          <strong>AI assisted generation:</strong> Your destination and preference
          data is sent to OpenAI to generate personalized itinerary
          recommendations
        </li>
        <li>
          <strong>Place data enrichment:</strong> We use Google Maps, Google
          Places, TripAdvisor, Foursquare, and AeroDataBox APIs to enrich your
          itinerary with location details, photos, ratings, and flight
          information
        </li>
        <li>
          <strong>Analytics:</strong> To understand how users interact with
          Wundra and improve the experience (opt-in only)
        </li>
        <li>
          <strong>Transactional emails:</strong> To send purchase confirmations,
          account notifications, and service updates
        </li>
        <li>
          <strong>Affiliate attribution:</strong> To credit referral partners
          when applicable
        </li>
      </ul>

      <h2>3. Third-Party Services</h2>
      <p>
        Wundra integrates with the following third-party services that may
        receive or process your data:
      </p>
      <ul>
        <li>
          <strong>Firebase</strong> (Google) — Authentication, Firestore
          database, Analytics, Cloud Functions, and hosting
        </li>
        <li>
          <strong>Google Maps &amp; Places API</strong> — Location search,
          geocoding, and place details
        </li>
        <li>
          <strong>OpenAI</strong> — AI assisted itinerary generation from your
          destination and preference inputs
        </li>
        <li>
          <strong>Stripe</strong> — Payment processing
        </li>
        <li>
          <strong>TripAdvisor</strong> — Venue ratings, reviews, and photos
        </li>
        <li>
          <strong>Foursquare</strong> — Place details, categories, and photos
        </li>
        <li>
          <strong>AeroDataBox</strong> — Flight data and airport information
        </li>
      </ul>
      <p>
        Each third-party service operates under its own privacy policy. We
        encourage you to review their respective policies.
      </p>

      <h2>4. Data Storage &amp; Security</h2>
      <p>
        Your data is stored in Google Firebase (Firestore) with access controls
        enforced by Firebase Security Rules. All data in transit is encrypted
        via TLS. We implement reasonable administrative, technical, and physical
        safeguards to protect your information, but no method of transmission or
        storage is 100% secure.
      </p>

      <h2>5. Cookies &amp; Local Storage</h2>
      <p>
        Wundra uses cookies and browser local storage for essential
        functionality and optional analytics. For full details, see our{' '}
        <Link to="/legal/cookies">Cookie Policy</Link>.
      </p>

      <h2>6. Your Rights</h2>
      <p>Depending on your jurisdiction, you may have the right to:</p>
      <ul>
        <li>
          <strong>Access</strong> the personal data we hold about you
        </li>
        <li>
          <strong>Correct</strong> inaccurate or incomplete data
        </li>
        <li>
          <strong>Delete</strong> your account and associated data
        </li>
        <li>
          <strong>Export</strong> your trip data in a portable format
        </li>
        <li>
          <strong>Opt out</strong> of analytics tracking via your profile
          settings
        </li>
      </ul>
      <p>
        To exercise any of these rights, contact us at{' '}
        <a href="mailto:hello@wundra.app">hello@wundra.app</a>.
      </p>

      <h2>7. Children's Privacy</h2>
      <p>
        Wundra is not intended for children under 13 years of age. We do not
        knowingly collect personal information from children under 13. If you
        believe we have collected such information, please contact us
        immediately.
      </p>

      <h2>8. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you
        of material changes by posting the updated policy on this page and
        updating the "Last updated" date. Your continued use of the Service
        after changes constitutes acceptance of the updated policy.
      </p>

      <h2>9. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy, contact us at:
      </p>
      <ul>
        <li>Grateful8 Consulting LLC</li>
        <li>
          Email: <a href="mailto:hello@wundra.app">hello@wundra.app</a>
        </li>
      </ul>
    </LegalPageLayout>
  );
}
